import React, { useState, useEffect } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';
import reportsService from '../api/reportsService';
import batchService from '../api/batchService';
import { formatCalendarDate } from '../utils/dateUtils';
import './Reports.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

// Calendar-date display, timezone-independent (shared helper).
const formatLocalDate = (value) => formatCalendarDate(value, undefined);

const Reports = () => {
  const [dateRange, setDateRange] = useState('30');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [selectedBatch, setSelectedBatch] = useState('');
  const [batches, setBatches] = useState([]);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({
    dashboard: null,
    trainerModules: null,
    trainerLocations: null,
    cancelledSessions: null,
    mockInterviews: null,
    missedLessons: null,
    timingAdherence: null,
    batchDuration: null
  });

  useEffect(() => {
    if (dateRange === 'custom' && (!customStart || !customEnd)) return;
    loadReportData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, selectedBatch, customStart, customEnd]);

  useEffect(() => {
    loadBatches();
    loadReportData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadBatches = async () => {
    try {
      const response = await batchService.getAllBatches();
      const batchData = response.data || [];
      setBatches(batchData);
    } catch (error) {
      console.error('Error loading batches:', error);
      setBatches([]);
    }
  };

  const getDateRange = () => {
    const today = new Date();
    const fmt = (d) => d.toISOString().split('T')[0];
    switch (dateRange) {
      case '7': {
        const from = new Date(today); from.setDate(from.getDate() - 7);
        return { startDate: fmt(from), endDate: fmt(today) };
      }
      case '30': {
        const from = new Date(today); from.setDate(from.getDate() - 30);
        return { startDate: fmt(from), endDate: fmt(today) };
      }
      case '90': {
        const from = new Date(today); from.setDate(from.getDate() - 90);
        return { startDate: fmt(from), endDate: fmt(today) };
      }
      case 'thisMonth':
        return { startDate: fmt(new Date(today.getFullYear(), today.getMonth(), 1)), endDate: fmt(today) };
      case 'lastMonth': {
        const first = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const last = new Date(today.getFullYear(), today.getMonth(), 0);
        return { startDate: fmt(first), endDate: fmt(last) };
      }
      case 'custom':
        return { startDate: customStart, endDate: customEnd };
      case 'all':
        return { startDate: '2020-01-01', endDate: fmt(today) };
      default: {
        const from = new Date(today); from.setDate(from.getDate() - 30);
        return { startDate: fmt(from), endDate: fmt(today) };
      }
    }
  };

  const loadReportData = async () => {
    setLoading(true);
    try {
      const batchId = selectedBatch || null;
      const { startDate, endDate } = getDateRange();
      const [
        dashboardRes,
        trainerModulesRes,
        trainerLocationsRes,
        cancelledRes,
        mockInterviewsRes,
        missedLessonsRes,
        timingRes,
        batchDurationRes
      ] = await Promise.all([
        reportsService.getDashboardSummary(startDate, endDate, batchId),
        reportsService.getSessionsByTrainerModule(startDate, endDate, batchId),
        reportsService.getTrainerSessionsByLocation(startDate, endDate, batchId),
        reportsService.getReassignedCancelledSessions(startDate, endDate, batchId),
        reportsService.getMockInterviewSessions(startDate, endDate, batchId),
        reportsService.getMissedLessons(startDate, endDate, batchId),
        reportsService.getSessionTimingAdherence(startDate, endDate, batchId),
        reportsService.getBatchDurationReport(batchId)
      ]);

      setData({
        dashboard: dashboardRes.data,
        trainerModules: trainerModulesRes.data,
        trainerLocations: trainerLocationsRes.data,
        cancelledSessions: cancelledRes.data,
        mockInterviews: mockInterviewsRes.data,
        missedLessons: missedLessonsRes.data,
        timingAdherence: timingRes.data,
        batchDuration: batchDurationRes.data
      });
    } catch (error) {
      console.error('Error loading report data:', error);
      // Set empty data to show "no data" states instead of loading forever
      setData({
        dashboard: { totalSessions: 0, completedSessions: 0, scheduledSessions: 0, cancelledSessions: 0, rescheduledSessions: 0, completionRate: '0' },
        trainerModules: {},
        trainerLocations: {},
        cancelledSessions: { totalCancelled: 0, sessions: [] },
        mockInterviews: {},
        missedLessons: { totalMissed: 0, sessions: [] },
        timingAdherence: { totalSessions: 0, completedOnTime: 0, sessions: [] },
        batchDuration: []
      });
    } finally {
      setLoading(false);
    }
  };

  const renderDashboard = () => {
    if (!data.dashboard) return <div>Loading...</div>;

    const { totalSessions, completedSessions, scheduledSessions, cancelledSessions, rescheduledSessions, completionRate } = data.dashboard;

    const doughnutData = {
      labels: ['Completed', 'Scheduled', 'Rescheduled', 'Cancelled'],
      datasets: [
        {
          data: [completedSessions || 0, scheduledSessions || 0, rescheduledSessions || 0, cancelledSessions || 0],
          backgroundColor: ['#4CAF50', '#2196F3', '#FF9800', '#F44336'],
          hoverBackgroundColor: ['#45a049', '#1976D2', '#e68900', '#da190b']
        }
      ]
    };

    return (
      <div className="dashboard-overview">
        <div className="summary-cards">
          <div className="summary-card">
            <h3>Total Sessions</h3>
            <p className="metric">{totalSessions}</p>
          </div>
          <div className="summary-card">
            <h3>Completed</h3>
            <p className="metric completed">{completedSessions}</p>
          </div>
          <div className="summary-card">
            <h3>Scheduled</h3>
            <p className="metric draft">{scheduledSessions || 0}</p>
          </div>
          <div className="summary-card">
            <h3>Rescheduled</h3>
            <p className="metric missed">{rescheduledSessions || 0}</p>
          </div>
          <div className="summary-card">
            <h3>Cancelled</h3>
            <p className="metric cancelled">{cancelledSessions}</p>
          </div>
          <div className="summary-card">
            <h3>Completion Rate</h3>
            <p className="metric rate">{completionRate}%</p>
          </div>
        </div>
        <div className="chart-container">
          <h3>Session Status Distribution</h3>
          <Doughnut data={doughnutData} options={{ responsive: true, maintainAspectRatio: false }} />
        </div>
      </div>
    );
  };

  const renderTrainerModules = () => {
    if (!data.trainerModules) return <div>Loading...</div>;

    const trainers = Object.values(data.trainerModules);
    if (trainers.length === 0) {
      return <div className="report-section"><h3>Sessions by Trainer</h3><p>No data for this period.</p></div>;
    }

    const sorted = [...trainers].sort((a, b) => b.totalSessions - a.totalSessions);
    const labels = sorted.map(t => t.trainerName);
    const completedData = sorted.map(t =>
      Object.values(t.batches).reduce((sum, b) => sum + b.completed, 0)
    );
    const scheduledData = sorted.map(t =>
      Object.values(t.batches).reduce((sum, b) => sum + b.scheduled, 0)
    );

    const chartData = {
      labels,
      datasets: [
        { label: 'Completed', data: completedData, backgroundColor: '#4CAF50' },
        { label: 'Scheduled', data: scheduledData, backgroundColor: '#2196F3' }
      ]
    };

    return (
      <div className="report-section">
        <h3>Sessions by Trainer</h3>
        <div className="chart-container">
          <Bar data={chartData} options={{ responsive: true, scales: { x: { stacked: true }, y: { stacked: true, beginAtZero: true } } }} />
        </div>
        <div className="data-table">
          <table>
            <thead>
              <tr>
                <th>Trainer</th>
                <th>Batch</th>
                <th>Completed</th>
                <th>Scheduled</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map(trainer =>
                Object.values(trainer.batches).map((batch, idx) => (
                  <tr key={`${trainer.trainerName}-${idx}`}>
                    {idx === 0 && (
                      <td rowSpan={Object.keys(trainer.batches).length}>
                        <strong>{trainer.trainerName}</strong>
                        <br /><small>{trainer.totalSessions} total</small>
                      </td>
                    )}
                    <td>{batch.batchName}</td>
                    <td>{batch.completed}</td>
                    <td>{batch.scheduled}</td>
                    <td>{batch.total}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderTrainerLocations = () => {
    if (!data.trainerLocations) return <div>Loading...</div>;

    return (
      <div className="report-section">
        <h3>Trainer Sessions by Location</h3>
        <div className="data-table">
          <table>
            <thead>
              <tr>
                <th>Trainer</th>
                <th>Center</th>
                <th>Location</th>
                <th>Sessions</th>
              </tr>
            </thead>
            <tbody>
              {Object.values(data.trainerLocations).map(trainer => 
                Object.values(trainer.locations).map((location, idx) => (
                  <tr key={`${trainer.trainerName}-${idx}`}>
                    <td>{trainer.trainerName}</td>
                    <td>{location.centerName}</td>
                    <td>{location.location}</td>
                    <td>{location.sessionCount}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderCancelledSessions = () => {
    if (!data.cancelledSessions) return <div>Loading...</div>;

    return (
      <div className="report-section">
        <h3>Reassigned or Cancelled Sessions Report</h3>
        <div className="summary-stat">
          <p><strong>Total Cancelled Sessions: {data.cancelledSessions.totalCancelled}</strong></p>
        </div>
        <div className="data-table">
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Trainer</th>
                <th>Batch</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              {data.cancelledSessions.sessions.map(session => (
                <tr key={session.id}>
                  <td>{formatLocalDate(session.date)}</td>
                  <td>{session.trainer}</td>
                  <td>{session.batch}</td>
                  <td>{session.notes || 'N/A'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderMockInterviews = () => {
    if (!data.mockInterviews) return <div>Loading...</div>;

    const batches = Object.values(data.mockInterviews);
    const labels = batches.map(b => b.batchName);
    const counts = batches.map(b => b.mockInterviewCount);

    const chartData = {
      labels,
      datasets: [
        {
          label: 'Mock Interview Sessions',
          data: counts,
          backgroundColor: '#36A2EB',
          borderColor: '#36A2EB',
          borderWidth: 1
        }
      ]
    };

    return (
      <div className="report-section">
        <h3>Mock Interview Sessions per Batch</h3>
        <div className="chart-container">
          <Bar data={chartData} options={{ responsive: true, scales: { y: { beginAtZero: true } } }} />
        </div>
        <div className="data-table">
          <table>
            <thead>
              <tr>
                <th>Batch</th>
                <th>Mock Interview Sessions</th>
              </tr>
            </thead>
            <tbody>
              {batches.map((batch, idx) => (
                <tr key={idx}>
                  <td>{batch.batchName}</td>
                  <td>{batch.mockInterviewCount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderMissedLessons = () => {
    if (!data.missedLessons) return <div>Loading...</div>;

    return (
      <div className="report-section">
        <h3>Missed Lessons Report</h3>
        <div className="summary-stat">
          <p><strong>Total Missed Sessions: {data.missedLessons.totalMissed}</strong></p>
        </div>
        <div className="data-table">
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Trainer</th>
                <th>Batch</th>
                <th>Module</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              {data.missedLessons.sessions.map(session => (
                <tr key={session.id}>
                  <td>{formatLocalDate(session.date)}</td>
                  <td>{session.trainer}</td>
                  <td>{session.batch}</td>
                  <td>{session.module}</td>
                  <td>{session.notes || 'N/A'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderBatchDuration = () => {
    if (!data.batchDuration) return <div>Loading...</div>;

    return (
      <div className="report-section">
        <h3>Batch Duration Report</h3>
        <div className="data-table">
          <table>
            <thead>
              <tr>
                <th>Batch</th>
                <th>Program</th>
                <th>Center</th>
                <th>Total Sessions</th>
                <th>Completed</th>
                <th>Start Date</th>
                <th>End Date</th>
                <th>Duration (Days)</th>
              </tr>
            </thead>
            <tbody>
              {data.batchDuration.map(batch => (
                <tr key={batch.batchId}>
                  <td>{batch.batchName}</td>
                  <td>{batch.program}</td>
                  <td>{batch.center || '-'}</td>
                  <td>{batch.totalSessions}</td>
                  <td>{batch.completedSessions}</td>
                  <td>{batch.startDate || 'N/A'}</td>
                  <td>{batch.endDate || 'N/A'}</td>
                  <td>{batch.durationDays}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard': return renderDashboard();
      case 'trainer-modules': return renderTrainerModules();
      case 'trainer-locations': return renderTrainerLocations();
      case 'cancelled': return renderCancelledSessions();
      case 'mock-interviews': return renderMockInterviews();
      case 'missed-lessons': return renderMissedLessons();
      case 'batch-duration': return renderBatchDuration();
      default: return renderDashboard();
    }
  };

  return (
    <div className="management-container">
      <div className="management-header">
        <div className="header-content">
          <div className="title-section">
            <h1 className="page-title">
              <span className="page-title-icon">📊</span>
              Dashboard
            </h1>
            <p className="page-subtitle">
              Analytics and insights for batch schedules
              {selectedBatch && (
                <span className="batch-filter-indicator">
                  📊 Filtered by: {batches.find(b => String(b.id) === String(selectedBatch))?.batch_name || 'Selected Batch'}
                </span>
              )}
            </p>
          </div>
          <div className="date-filters">
            <select value={selectedBatch} onChange={(e) => setSelectedBatch(e.target.value)}>
              <option value="">All Batches ({batches.length} available)</option>
              {batches.map(batch => (
                <option key={batch.id} value={batch.id}>
                  {batch.batch_name} - {batch.center?.name || 'No Center'}
                </option>
              ))}
            </select>
            <select value={dateRange} onChange={(e) => setDateRange(e.target.value)}>
              <option value="7">Last 7 days</option>
              <option value="30">Last 30 days</option>
              <option value="90">Last 90 days</option>
              <option value="thisMonth">This month</option>
              <option value="lastMonth">Last month</option>
              <option value="all">All time</option>
              <option value="custom">Custom range</option>
            </select>
            {dateRange === 'custom' && (
              <>
                <input type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)} />
                <span style={{ alignSelf: 'center' }}>to</span>
                <input type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} />
              </>
            )}
          </div>
        </div>
      </div>

      <div className="reports-tabs">
        <button
          className={activeTab === 'dashboard' ? 'active' : ''}
          onClick={() => setActiveTab('dashboard')}
        >
          📈 Overview
        </button>
        <button 
          className={activeTab === 'trainer-modules' ? 'active' : ''} 
          onClick={() => setActiveTab('trainer-modules')}
        >
          👩‍🏫 Trainer Sessions
        </button>
        <button 
          className={activeTab === 'trainer-locations' ? 'active' : ''} 
          onClick={() => setActiveTab('trainer-locations')}
        >
          📍 Location Coverage
        </button>
        <button 
          className={activeTab === 'cancelled' ? 'active' : ''} 
          onClick={() => setActiveTab('cancelled')}
        >
          ❌ Cancelled Sessions
        </button>
        <button 
          className={activeTab === 'mock-interviews' ? 'active' : ''} 
          onClick={() => setActiveTab('mock-interviews')}
        >
          🎤 Mock Interviews
        </button>
        <button 
          className={activeTab === 'missed-lessons' ? 'active' : ''} 
          onClick={() => setActiveTab('missed-lessons')}
        >
          ⚠️ Missed Lessons
        </button>
        <button 
          className={activeTab === 'batch-duration' ? 'active' : ''} 
          onClick={() => setActiveTab('batch-duration')}
        >
          ⏱️ Batch Duration
        </button>
      </div>

      <div className="reports-content">
        {loading ? (
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Loading reports...</p>
          </div>
        ) : (
          renderContent()
        )}
      </div>
    </div>
  );
};

export default Reports;
